import { useQuery } from "@tanstack/react-query";
import { useDynamicSupabase } from "@/hooks/useDynamicSupabase";

export interface EmployeePackagePerformance {
  employeeId: string;
  employeeName: string;
  totalPackagesSold: number;
  totalRevenue: number;
  conversionRate: number;
  commission: number;
  averageRating: number;
  rank: number;
}

export const usePackageEmployeePerformance = (employeeId?: string) => {
  const { supabase } = useDynamicSupabase();
  
  return useQuery({
    queryKey: ["employee-package-performance", employeeId],
    queryFn: async (): Promise<EmployeePackagePerformance[]> => {
      // Get bookings data
      const { data: bookings, error: bookingsError } = await supabase
        .from("package_bookings")
        .select(`
          *,
          employee:employees(id, name)
        `) as any;

      if (bookingsError) throw bookingsError;

      // Get employees
      const { data: employees, error: employeesError } = await supabase
        .from("employees")
        .select("*");

      if (employeesError) throw employeesError;

      // Group by employee
      const employeeMap = new Map<string, EmployeePackagePerformance>();

      bookings?.forEach((booking: any) => {
        const empId = booking.employee?.id;
        if (!empId) return;
        
        if (!employeeMap.has(empId)) {
          const emp = employees?.find(e => e.id === empId);
          employeeMap.set(empId, {
            employeeId: empId,
            employeeName: emp?.name || 'Unknown',
            totalPackagesSold: 0,
            totalRevenue: 0,
            conversionRate: 0,
            commission: 0,
            averageRating: 0,
            rank: 0
          });
        }

        const empData = employeeMap.get(empId)!;
        
        if (booking.booking_status === 'confirmed' || booking.booking_status === 'completed') {
          empData.totalPackagesSold++;
          empData.totalRevenue += booking.total_price || 0;
          // commission and rating would come from package_bookings_enhanced when populated
        }
      });

      // Convert to array and sort
      let result = Array.from(employeeMap.values())
        .sort((a, b) => b.totalRevenue - a.totalRevenue)
        .map((emp, index) => ({ ...emp, rank: index + 1, averageRating: Number(emp.averageRating.toFixed(2)) }));

      // Filter by employee if specified
      if (employeeId) {
        result = result.filter(emp => emp.employeeId === employeeId);
      }

      return result;
    }
  });
};
